import { useState, useMemo, useEffect } from "react";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Wallet,
  ArrowLeft,
  Repeat,
  Send,
  ArrowUpFromLine,
  ArrowDownFromLine,
  Coins,
} from "lucide-react";
import { makeRequest } from "@/utils/makeRequest";
import { useNavigate, useParams } from "react-router-dom";

import { useAppSelector } from "@/store/hooks";
import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";
import { toast } from "sonner";
import { ActionType, type ModalType } from "@/interfaces/network";
import { formatCurrency } from "@/utils";
import Loader from "@/components/loader/Loader";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/services/queryClient";
import type { WalletAsset } from "@/interfaces/asset";
import { ErrorState } from "@/components/ErrorState";
import { useSystemWalletDetails } from "@/hooks";

type ActionResult = {
  type: ActionType;
  success: boolean;
  message: string;
  meta?: any;
};

const AdminWalletDetailsPage = () => {
  const { systemType } = useParams();
  const { theme } = useAppSelector((state) => state.ui);

  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState<"name" | "date" | "balance">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  //
  const [selectedAsset, setSelectedAsset] = useState<WalletAsset>();
  const [isLoading, setIsLoading] = useState(false);

  // Form states
  const [withdrawAmount, setWithdrawAmount] = useState("");

  const [verificationOpen, setVerificationOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<ActionType | null>(null);
  const [actionPayload, setActionPayload] = useState<any>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!systemType) return;
  }, [systemType]);

  const executeAction = async (
    type: ActionType,
    auth?: { otp: string; securityCode: string },
  ): Promise<ActionResult> => {
    setIsLoading(true);

    try {
      switch (type) {
        case "withdraw": {
          await new Promise((r) => setTimeout(r, 1500));

          return {
            type,
            success: true,
            message: `Withdrawal of ${withdrawAmount} ${selectedAssetData?.token?.symbol ?? ""} initiated successfully!`,
          };
        }

        case "swap": {
          const { amount } = actionPayload;

          await makeRequest.post("/blockchain/swap/system", {
            tokenFrom: actionPayload.tokenFrom,
            tokenTo: actionPayload.tokenTo,
            amountFrom: actionPayload.amount,
            otp: auth?.otp,
            securityCode: auth?.securityCode,
            systemWalletId: wallet?.id,
            systemWalletAddress: wallet?.address,
          });

          await queryClient.invalidateQueries({
            queryKey: ["wallet:details", systemType],
          });

          return {
            type,
            success: true,
            message: `Swap of ${amount} completed`,
          };
        }

        default:
          return {
            type,
            success: false,
            message: "Unsupported action type",
          };
      }
    } catch (err: any) {
      return {
        type,
        success: false,
        message: getErrorMessage(err) || "Action failed",
      };
    } finally {
      setIsLoading(false);
    }
  };

  const itemsPerPage = 10;

  const {
    data: wallet,
    isLoading: isLoadingWallet,
    isError,
    refetch,
  } = useSystemWalletDetails(systemType!);

  console.log("WALLET", wallet);

  const assets: WalletAsset[] = wallet?.assets || [];

  /**
   * IMPORTANT:
   * The backend now calculates the authoritative ledger balance
   * and returns it as `asset.ledgerBalance`.
   *
   * Do not use asset.balance or account.balance directly here.
   */
  const getAssetBalance = (asset: WalletAsset): string => {
    return asset.ledgerBalance ?? "0";
  };

  const formatLedgerBalance = (
    value: string | number | null | undefined,
  ): string => {
    const amount = Number(value ?? 0);

    if (!Number.isFinite(amount)) {
      return "0";
    }

    return amount.toLocaleString(undefined, {
      maximumFractionDigits: 8,
    });
  };

  const selectedAssetData = assets.find((a) => a.id === selectedAsset?.id);

  useEffect(() => {
    if (assets.length > 0 && !selectedAsset) {
      setSelectedAsset(assets[0]);
    }
  }, [assets, selectedAsset]);

  // Filtering + sorting
  const filteredAssets = useMemo(() => {
    let result = assets.filter((asset) => {
      const search = searchQuery.toLowerCase();

      const tokenName = asset?.token?.name ?? "";
      const networkName = asset?.network?.name ?? "";
      const currency = asset?.currency ?? "";
      const contractAddress = asset?.token.address ?? "";

      const matchesSearch =
        tokenName.toLowerCase().includes(search) ||
        networkName.toLowerCase().includes(search) ||
        currency.toLowerCase().includes(search) ||
        contractAddress.toLowerCase().includes(search);

      /**
       * ledgerBalance is already a human-readable decimal value
       * returned by the backend.
       *
       * Example:
       * "0.004" BNB
       */
      const balance = Number(getAssetBalance(asset));

      const matchesFilter =
        filter === "all" ||
        (filter === "active" && asset.isActive) ||
        (filter === "inactive" && !asset.isActive) ||
        (filter === "fiat" && asset.token?.isFiat) ||
        (filter === "crypto" && !asset.token?.isFiat) ||
        (filter === "zero" && balance === 0) ||
        (filter === "nonzero" && balance > 0);

      return matchesSearch && matchesFilter;
    });

    result.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "name":
          comparison = (a?.token?.name ?? "").localeCompare(
            b?.token?.name ?? "",
          );
          break;

        case "date":
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;

        case "balance":
          comparison = Number(getAssetBalance(a)) - Number(getAssetBalance(b));
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [assets, searchQuery, filter, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);

  const paginatedAssets = filteredAssets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch?.();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  if (isLoadingWallet) return <Loader type="bounce" />;

  return (
    <div className="min-h-screen bg-background px-4 lg:px-6">
      <div className="max-w-7xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {/* Header */}
        <div className="flex justify-end mb-8">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 bg-card rounded-lg text-muted-foreground hover:text-foreground"
          >
            <RefreshCw
              className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1
              className={`text-2xl font-bold flex items-center gap-3 ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
            >
              <Wallet className="w-6 h-6 text-primary" />
              {`${wallet?.systemType.toUpperCase()} Wallet`}
            </h1>
            <p
              className={`text-sm mt-1 ${theme === "dark" ? "text-muted-foreground" : "text-gray-500"}`}
            >
              Manage your assets and view balances
            </p>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:items-center sm:justify-end">
            <button
              onClick={() => navigate(`/admin/deposit/${systemType}`)}
              className="flex items-center justify-center gap-2 text-sm py-2 px-4 rounded-lg bg-[#F0B90B] text-black font-medium hover:brightness-110 transition-all w-full sm:w-auto"
            >
              <ArrowDownFromLine className="w-4 h-4" />
              Deposit
            </button>

            <button
              onClick={() => navigate(`/admin/withdraw/${systemType} `)}
              className={`flex items-center justify-center gap-2 text-sm py-2 px-4 rounded-lg border font-medium transition-colors w-full sm:w-auto ${
                theme === "dark"
                  ? "border-border text-foreground hover:bg-muted"
                  : "border-gray-200 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <ArrowUpFromLine className="w-4 h-4" />
              Withdraw
            </button>

            <button
              onClick={() => navigate(`/admin/transfer/${systemType}`)}
              className={`flex items-center justify-center gap-2 text-sm py-2 px-4 rounded-lg border font-medium transition-colors w-full sm:w-auto ${
                theme === "dark"
                  ? "border-border text-foreground hover:bg-muted"
                  : "border-gray-200 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <Send className="w-4 h-4" />
              Transfer
            </button>

            <button
              onClick={() => navigate(`/admin/swap/${systemType}`)}
              className={`flex items-center justify-center gap-2 text-sm py-2 px-4 rounded-lg border font-medium transition-colors w-full sm:w-auto ${
                theme === "dark"
                  ? "border-border text-foreground hover:bg-muted"
                  : "border-gray-200 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <Repeat className="w-4 h-4" />
              Swap
            </button>

            {/* NEW: Mint */}
            {systemType === "treasury" && (
              <button
                onClick={() => navigate(`/admin/mint/${systemType}`)}
                className={`flex items-center justify-center gap-2 text-sm py-2 px-4 rounded-lg border font-medium transition-colors w-full sm:w-auto ${
                  theme === "dark"
                    ? "border-border text-foreground hover:bg-muted"
                    : "border-gray-200 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Coins className="w-4 h-4" />
                Mint
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Balance */}
          <div className="nex-card p-4">
            <p className="text-xs text-muted-foreground">Total Balance</p>
            <p className="text-2xl font-bold text-foreground">
              {wallet?.totalBalanceCurrency ?? "USD"}{" "}
              {formatCurrency(Number(wallet?.totalBalance ?? 0))}
            </p>
          </div>

          {/* Assets Count */}
          <div className="nex-card p-4">
            <p className="text-xs text-muted-foreground">Assets</p>
            <p className="text-2xl font-bold text-foreground">
              {assets.length}
            </p>
          </div>

          {/* 24h Inflow */}
          <div className="nex-card p-4">
            <p className="text-xs text-muted-foreground">24h Inflow</p>
            <p className="text-2xl font-bold text-emerald-500">
              +{formatCurrency(0)}
            </p>
          </div>

          {/* 24h Outflow */}
          <div className="nex-card p-4">
            <p className="text-xs text-muted-foreground">24h Outflow</p>
            <p className="text-2xl font-bold text-red-500">
              -{formatCurrency(0)}
            </p>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            {/* Search */}
            <div className="relative w-full lg:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />

              <input
                type="text"
                placeholder="Search token, currency, contract..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className=" w-full h-10 pl-10 pr-4 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 "
              />
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              {/* Asset Filter */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Filter className="hidden sm:block w-4 h-4 shrink-0 text-muted-foreground" />

                <select
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className=" w-full sm:w-auto min-w-[150px] h-10 px-3 rounded-lg bg-card border border-border text-sm text-foreground outline-none cursor-pointer focus:border-primary focus:ring-2 focus:ring-primary/20 "
                >
                  <option value="all">All Assets</option>
                  <option value="fiat">Fiats</option>
                  <option value="crypto">Cryptos</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="zero">Zero Balance</option>
                  <option value="nonzero">Non-Zero Balance</option>
                </select>
              </div>

              {/* Sort */}
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split("-");

                  setSortBy(field as "name" | "date" | "balance");
                  setSortOrder(order as "asc" | "desc");
                  setCurrentPage(1);
                }}
                className=" w-full sm:w-auto min-w-[170px] h-10 px-3 rounded-lg bg-card border border-border text-sm text-foreground outline-none cursor-pointer focus:border-primary focus:ring-2 focus:ring-primary/20 "
              >
                <option value="date-desc">Newest</option>
                <option value="date-asc">Oldest</option>
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="balance-desc">Highest Balance</option>
                <option value="balance-asc">Lowest Balance</option>
              </select>
            </div>
          </div>

          {/* Result count */}
          <div className="flex items-center justify-between">
            <p className="text-xs sm:text-sm text-muted-foreground">
              {filteredAssets.length}{" "}
              {filteredAssets.length === 1 ? "asset" : "assets"} found
            </p>

            {(searchQuery || filter !== "all") && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setFilter("all");
                  setCurrentPage(1);
                }}
                className=" text-xs sm:text-sm text-primary hover:underline transition-colors "
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Assets Table */}
        <div className="nex-card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <p className="text-sm text-muted-foreground">Loading assets...</p>
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
              <div className="mb-3 rounded-full bg-muted p-3">
                <Search className="w-5 h-5 text-muted-foreground" />
              </div>

              <h3 className="text-sm font-semibold text-foreground">
                No assets found
              </h3>

              <p className="mt-1 max-w-sm text-xs sm:text-sm text-muted-foreground">
                No assets match your current search or filter criteria.
              </p>

              {(searchQuery || filter !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setFilter("all");
                    setCurrentPage(1);
                  }}
                  className=" mt-4 text-sm font-medium text-primary hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Table wrapper */}
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="whitespace-nowrap text-left py-3 px-4 text-xs sm:text-sm font-medium text-muted-foreground">
                        #
                      </th>

                      <th className="whitespace-nowrap text-left py-3 px-4 text-xs sm:text-sm font-medium text-muted-foreground">
                        Network
                      </th>

                      <th className="whitespace-nowrap text-left py-3 px-4 text-xs sm:text-sm font-medium text-muted-foreground">
                        Asset
                      </th>

                      <th className="whitespace-nowrap text-left py-3 px-4 text-xs sm:text-sm font-medium text-muted-foreground">
                        Symbol
                      </th>

                      <th className="whitespace-nowrap text-left py-3 px-4 text-xs sm:text-sm font-medium text-muted-foreground">
                        Currency
                      </th>

                      <th className="whitespace-nowrap text-left py-3 px-4 text-xs sm:text-sm font-medium text-muted-foreground">
                        Balance
                      </th>

                      <th className="whitespace-nowrap text-left py-3 px-4 text-xs sm:text-sm font-medium text-muted-foreground">
                        Status
                      </th>

                      <th className="whitespace-nowrap text-left py-3 px-4 text-xs sm:text-sm font-medium text-muted-foreground">
                        Created
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedAssets.map((asset, index: number) => (
                      <tr
                        key={asset.id}
                        className=" border-b last:border-b-0 hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        {/* ID */}
                        <td className="whitespace-nowrap py-4 px-4 text-sm font-medium">
                          {(currentPage - 1) * itemsPerPage + index + 1}.
                        </td>

                        {/* Network */}
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {asset?.token?.blockchainNetwork?.code ?? "—"}
                            </span>
                          </div>
                        </td>

                        {/* Asset */}
                        <td className="py-4 px-4">
                          <div className="flex flex-col min-w-[140px]">
                            <span className="text-sm font-medium text-foreground">
                              {asset?.token?.name ?? "Unknown Asset"}
                            </span>

                            {asset?.token?.isNative && (
                              <span className="mt-0.5 text-[11px] text-muted-foreground">
                                Native Asset
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Symbol */}
                        <td className="whitespace-nowrap py-4 px-4">
                          <span className="text-sm font-medium">
                            {asset?.token?.symbol ?? "—"}
                          </span>
                        </td>

                        {/* Currency */}
                        <td className="whitespace-nowrap py-4 px-4">
                          <span className="text-sm text-muted-foreground">
                            {asset?.currency ?? "—"}
                          </span>
                        </td>

                        {/* Balance */}
                        <td className="py-4 px-4">
                          <div className="flex flex-col min-w-[130px]">
                            <span className="text-sm font-semibold text-foreground">
                              {formatLedgerBalance(asset?.ledgerBalance)}
                            </span>

                            <span className="mt-0.5 text-xs text-muted-foreground">
                              {asset?.token?.symbol ?? asset?.currency ?? "—"}
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="whitespace-nowrap py-4 px-4">
                          <span
                            className={` inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full
                      ${
                        asset.isActive
                          ? "bg-emerald-500/10 text-emerald-500"
                          : "bg-red-500/10 text-red-500"
                      }
                    `}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full
                        ${asset.isActive ? "bg-emerald-500" : "bg-red-500"}
                      `}
                            />

                            {asset.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>

                        {/* Created */}
                        <td className="whitespace-nowrap py-4 px-4 text-sm text-muted-foreground">
                          {new Date(asset.createdAt).toLocaleDateString(
                            undefined,
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col gap-4 border-t p-4 sm:flex-row sm:items-center sm:justify-between">
                {/* Pagination info */}
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Showing{" "}
                  <span className="font-medium text-foreground">
                    {(currentPage - 1) * itemsPerPage + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium text-foreground">
                    {Math.min(
                      currentPage * itemsPerPage,
                      filteredAssets.length,
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium text-foreground">
                    {filteredAssets.length}
                  </span>
                </p>

                {/* Pagination controls */}
                <div className="flex items-center justify-between gap-2 sm:justify-end">
                  {/* Previous */}
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className=" inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page numbers */}
                  <div className="flex items-center gap-1 overflow-x-auto">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .slice(
                        Math.max(0, currentPage - 3),
                        Math.min(totalPages, currentPage + 2),
                      )
                      .map((page) => (
                        <button
                          key={page}
                          type="button"
                          onClick={() => setCurrentPage(page)}
                          className={` inline-flex h-9 min-w-9 items-center justify-center rounded-lg px-2.5 text-sm font-medium transition-colors
                    ${
                      currentPage === page
                        ? "bg-primary text-primary-foreground"
                        : "bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
                    }
                  `}
                        >
                          {page}
                        </button>
                      ))}
                  </div>

                  {/* Next */}
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40 "
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <AdminVerificationDialog
        open={verificationOpen}
        onOpenChange={setVerificationOpen}
        systemType={systemType}
        onSuccess={async ({ otp, securityCode }) => {
          const result = await executeAction(pendingAction!, {
            otp,
            securityCode,
          });

          if (!result.success) {
            throw new Error(result.message || "Action failed");
          }

          toast.success(result.message);

          // setVerificationOpen(false);
          setPendingAction(null);
          setActionPayload(null);

          navigate("/admin#recent-transactions");
        }}
      />
    </div>
  );
};

export default AdminWalletDetailsPage;
